import React, { useState } from 'react';
import { Grid, Typography, Card, CardHeader, CardContent } from '@mui/material';

import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getSPDLeadership,
  patchSPDLeadership,
  postSPDLeadership,
  getSPDLeadershipPositions,
  deleteSPDLeadership,
  replaceExistingLeadership,
  SPDLeadershipData,
  SPDLeadershipRequest,
  SPDLeadershipUpdateRequest,
  SPDLeadershipReplaceRequest,
  SPDLeadershipDeleteRequest,
} from '../../../endpoints/chaptersTs';
import { roles } from '../../../utils/variables';
import PositionDisplay from '../../../Components/PositionDisplay';
import AddPosition from '../../../Components/AddPosition';
import dayjs from 'dayjs';
import { LeadershipPeer } from '../../../utils/leadershipSelfRemovalWarning';

interface SPDLeadershipProps {
  id: string;
  spdAccess: boolean;
}

interface SaveData {
  id: string;
  positionId?: string;
  committeeId?: string;
  action: 'newAppointment' | 'editDates' | 'removeRecord';
  userId?: string;
  startDate?: string | null;
  endDate?: string | null;
}

const SPDLeadership: React.FC<SPDLeadershipProps> = ({ id, spdAccess }) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorSaving, setErrorSaving] = useState<string | null>(null);

  // Helper function to format date to YYYY-MM-DD format
  const formatDateForAPI = (
    dateString: string | null | undefined
  ): string | null => {
    if (!dateString) return null;
    const date = dayjs(dateString);
    return date.isValid() ? date.format('YYYY-MM-DD') : null;
  };

  const {
    data: leadershipData,
    isFetching,
    refetch,
  } = useApiGet<SPDLeadershipData>(
    ['spd-leadership', id],
    getSPDLeadership,
    {
      enabled: true,
      retry: 1,
    },
    {
      id,
    }
  );

  const { data: positionsData } = useApiGet<any>(
    ['spd-leadership-positions'],
    getSPDLeadershipPositions,
    {
      enabled: true,
      retry: 1,
    }
  );

  const { mutate: updateLeadership } = useApiSend(
    patchSPDLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: any) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: deleteLeadership } = useApiSend(
    deleteSPDLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: any) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: postLeadership } = useApiSend(
    postSPDLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: any) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: replaceLeadership } = useApiSend(
    replaceExistingLeadership,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: any) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const onSavePosition = (position: SaveData) => {
    setIsSaving(true);
    setErrorSaving(null);

    if (position.action === 'newAppointment') {
      //Handle post
      if (!position.id) {
        //Handle new appointment
        const requestData: SPDLeadershipRequest = {
          formId: id,
          user_id: position.userId || '',
          start_date: formatDateForAPI(position.startDate) || '',
          end_date: null,
          position_id: position.positionId || '',
          committee_id: position.committeeId || '',
        };
        postLeadership(requestData);
      } else {
        const replaceData: SPDLeadershipReplaceRequest = {
          formId: id,
          id: position.id,
          user_id: position.userId || '',
          start_date: formatDateForAPI(position.startDate) || '',
          end_date: null,
        };
        replaceLeadership(replaceData);
      }
    } else if (position.action === 'editDates') {
      //Handle patch
      const updateData: SPDLeadershipUpdateRequest = {
        formId: id,
        id: position.id,
        start_date: formatDateForAPI(position.startDate) || '',
        end_date: formatDateForAPI(position.endDate),
      };
      updateLeadership(updateData);
    } else if (position.action === 'removeRecord') {
      //Handle delete
      const deleteData: SPDLeadershipDeleteRequest = {
        formId: id,
        id: position.id,
      };
      deleteLeadership(deleteData);
    }
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!leadershipData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  const canEdit = (): boolean => {
    if (spdAccess) {
      const userRole = localStorage.getItem('role');
      const rolesAllowed = [roles.Admin, roles.Support, roles.SPDOfficer];
      return rolesAllowed.includes(userRole || '');
    }
    return false;
  };
  const leadershipPeers: LeadershipPeer[] = [
    ...(leadershipData.officers || []),
    ...(leadershipData.assistants || []),
    ...(leadershipData.qbe_users || []),
  ].map((entry) => ({
    id: entry.id,
    user_id: entry.user_id,
  }));

  return (
    <Grid container>
      <Grid item xs={12} md={6} sx={{ padding: 2 }}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Current Officers" />
          <CardContent>
            <Grid container spacing={2}>
              {leadershipData.officers
                ?.sort((a, b) => a.order - b.order)
                .map((officer) => (
                  <PositionDisplay
                    id={officer.id}
                    position={officer.position}
                    positionId={officer.position_id}
                    committeeId={officer.committee_id}
                    name={officer.name}
                    startDate={officer.start_date}
                    endDate={officer.end_date}
                    canEdit={canEdit()}
                    isSaving={isSaving}
                    errorSavingText={errorSaving}
                    onSave={onSavePosition}
                    userId={officer.user_id}
                    key={officer.id}
                    pageFrom={'spd-leadership'}
                    spdChapterId={id}
                    leadershipPeers={leadershipPeers}
                  />
                ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6} sx={{ padding: 2 }}>
        <Grid item container xs={12}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2, width: '100%' }}>
            <CardHeader title="Executive Assistants" sx={{ p: 1 }} />
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                {leadershipData.assistants
                  ?.sort((a, b) => a.order - b.order)
                  .map((assistant) => (
                    <PositionDisplay
                      id={assistant.id}
                      position={assistant.position}
                      positionId={assistant.position_id}
                      committeeId={assistant.committee_id}
                      name={assistant.name}
                      startDate={assistant.start_date}
                      endDate={assistant.end_date}
                      canEdit={canEdit()}
                      isSaving={isSaving}
                      errorSavingText={errorSaving}
                      onSave={onSavePosition}
                      userId={assistant.user_id}
                      pageFrom={'spd-leadership'}
                      key={assistant.id}
                      spdChapterId={id}
                      leadershipPeers={leadershipPeers}
                    />
                  ))}
                {leadershipData.assistants.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body1" textAlign={'center'}>
                      No assistants assigned
                    </Typography>
                  </Grid>
                )}
                {canEdit() && (
                  <Grid item xs={12} sx={{ padding: 2 }}>
                    <AddPosition
                      buttonText={'Add Executive Assistant'}
                      positions={positionsData}
                      committeeId={leadershipData?.officers[0]?.committee_id}
                      onAdd={onSavePosition}
                      isSaving={isSaving}
                      savingError={errorSaving}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item container xs={12} paddingTop={2}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2, width: '100%' }}>
            <CardHeader title="QBE Users" sx={{ p: 1 }} />
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                {leadershipData.qbe_users.map((user) => (
                  <PositionDisplay
                    id={user.id}
                    position={user.position}
                    positionId={user.position_id}
                    committeeId={user.committee_id}
                    name={user.name}
                    startDate={user.start_date}
                    endDate={user.end_date}
                    canEdit={canEdit()}
                    isSaving={isSaving}
                    errorSavingText={errorSaving}
                    onSave={onSavePosition}
                    userId={user.user_id}
                    pageFrom={'spd-leadership'}
                    key={user.id}
                    spdChapterId={id}
                    leadershipPeers={leadershipPeers}
                  />
                ))}
                {leadershipData.qbe_users.length === 0 && (
                  <Grid item xs={12} textAlign={'center'}>
                    <Typography variant="body1">
                      No QBE users assigned
                    </Typography>
                  </Grid>
                )}
                {canEdit() && (
                  <Grid item xs={12} sx={{ padding: 2 }}>
                    <AddPosition
                      buttonText={'Add QBE User'}
                      positions={[
                        {
                          id: 'f074749e730244a99524b43ac9a4b786',
                          name: 'Member',
                          order: 0,
                        },
                      ]}
                      committeeId={''}
                      onAdd={onSavePosition}
                      isSaving={isSaving}
                      savingError={errorSaving}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SPDLeadership;
